import React from "react";
import PropTypes from "prop-types";
import QueueAnim from "rc-queue-anim";
import { push } from "connected-react-router";
import Page404 from "routes/404";
import RaisedButton from "@mui/material/Button";
import { withStyles } from "@mui/styles";
import CircularProgress from "components/EnhancedCircularProgress";
import EnhancedSnackBar from "components/EnhancedSnackBar";
import { authLogout } from "stores/actions/auth";
import {
  resolveCashboxApprovalLink,
  fetchCashboxApproval
} from "services/cashboxApprovalService";
import { CASHBOX_APPROVAL_TOKEN_KEY } from "constants/Paths";
import { attemptAppHandoff } from "./appHandoff";
import ConfirmResponse from "./ConfirmResponse";
import {
  LINK_STATE_READY,
  LINK_STATE_LOGIN_REQUIRED,
  APPROVAL_STATUS_APPROVED,
  APPROVAL_STATUS_REJECTED
} from "./constants";
import { styles } from "./styles";

/**
 * The page the WhatsApp approval link opens. Part of the portal, but outside
 * /app and in no menu: it is reached only through that link.
 *
 * A dead link — answered, expired, a failed delivery, or a token that never
 * existed — renders the portal 404, the same one an address that was never a page
 * gets. A missing or wrong session lands on the ordinary login page, unadorned.
 */
class CashboxApproval extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      isLoading: true,
      is_dead: false,
      order_id: null,
      approval: null,
      answered: false,
      // null while no modal is open, otherwise true for approve, false for reject.
      is_approve: null
    };
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    // The app is an optimisation, never a requirement: the card below is a
    // complete fallback, so the page carries on loading either way.
    attemptAppHandoff(this.props.match.params.token);
    this.resolveLink();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  resolveLink = () => {
    const token = this.props.match.params.token;

    this.props
      .dispatch(resolveCashboxApprovalLink(token))
      .then(data => {
        if (!this._isMounted) {
          return;
        }

        if (data.state === LINK_STATE_READY) {
          sessionStorage.removeItem(CASHBOX_APPROVAL_TOKEN_KEY);
          this.loadApproval(data.order_id);
          return;
        }

        if (data.state === LINK_STATE_LOGIN_REQUIRED) {
          // Stored before the session is cleared, so the login page knows to come
          // back here. Nobody signed in and the wrong person signed in are one
          // state: the same login page, unadorned, in both cases.
          sessionStorage.setItem(CASHBOX_APPROVAL_TOKEN_KEY, token);
          this.props.dispatch(authLogout());
          this.props.dispatch(push("/login"));
          return;
        }

        this.showDead();
      })
      .catch(() => this.showDead());
  };

  loadApproval = order_id => {
    this.props
      .dispatch(fetchCashboxApproval(order_id))
      .then(data => {
        if (!this._isMounted) {
          return;
        }

        if (!data.show_approval_card) {
          this.showDead();
          return;
        }

        this.setState({
          isLoading: false,
          order_id,
          approval: data.cashbox_approval
        });
      })
      .catch(() => this.showDead());
  };

  showDead = () => {
    sessionStorage.removeItem(CASHBOX_APPROVAL_TOKEN_KEY);
    this.setState({ isLoading: false, is_dead: true, approval: null });
  };

  handleOpen = is_approve => () => {
    this.setState({ is_approve });
  };

  handleClose = () => {
    this.setState({ is_approve: null });
  };

  handleAnswered = approval => {
    this.setState({ approval, answered: true, is_approve: null });
  };

  /**
   * A refused answer usually means the request moved while the page was open —
   * answered elsewhere, or the driver failed the delivery. Reload the card and
   * show whatever it now says rather than leaving a dead button on screen.
   */
  handleFailed = () => {
    this.setState({ is_approve: null });

    this.props
      .dispatch(fetchCashboxApproval(this.state.order_id))
      .then(data => {
        if (!this._isMounted) {
          return;
        }

        if (!data.cashbox_approval) {
          this.showDead();
          return;
        }

        this.setState({
          approval: data.cashbox_approval,
          answered: !data.show_approval_card
        });
      })
      .catch(() => {});
  };

  renderAmounts = () => {
    const { classes } = this.props;

    return this.state.approval.amounts.map(amount => (
      <div key={amount.currency_id} className={classes.amount_row}>
        <span className={classes.amount_value}>
          {Number(amount.amount).toLocaleString()}
        </span>
        <span className={classes.amount_currency}>{amount.currency}</span>
      </div>
    ));
  };

  renderAnswer = () => {
    const { classes } = this.props;
    const { approval } = this.state;
    const is_approved = approval.status === APPROVAL_STATUS_APPROVED;
    const is_rejected = approval.status === APPROVAL_STATUS_REJECTED;

    return (
      <div className="text-center">
        <span
          className={"material-icons " + classes.result_icon}
          style={{
            color: is_rejected ? "#f44336" : is_approved ? "#4caf50" : "#9e9e9e"
          }}
        >
          {is_rejected ? "cancel" : is_approved ? "check_circle" : "info"}
        </span>
        <h5 className="mt-0">{approval.message}</h5>
      </div>
    );
  };

  renderRequest = () => {
    const { classes } = this.props;
    const { approval } = this.state;

    return (
      <div>
        <div style={{ margin: "20px 0" }}>{this.renderAmounts()}</div>
        <div className="row">
          <div className="col-6">
            <RaisedButton
              variant="contained"
              fullWidth
              className={classes.reject_button}
              onClick={this.handleOpen(false)}
              disabled={!approval.can_respond}
            >
              Reject
            </RaisedButton>
          </div>
          <div className="col-6">
            <RaisedButton
              variant="contained"
              fullWidth
              className={classes.approve_button}
              onClick={this.handleOpen(true)}
              disabled={!approval.can_respond}
            >
              Confirm
            </RaisedButton>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      isLoading,
      is_dead,
      approval,
      answered,
      is_approve,
      order_id
    } = this.state;

    if (is_dead) {
      return <Page404 />;
    }

    return (
      <div className="container-fluid page-dashboard" style={{ height: "auto" }}>
        <QueueAnim type="bottom" className="ui-animate">
          {isLoading ? (
            <div key="1" style={{ height: "100%" }}>
              <CircularProgress />
            </div>
          ) : (
            <div key="1">
              <article className="article">
                <div className="row">
                  <div className="col-12">
                    <h1 style={{ textAlign: "right", margin: "0 0 5px 0" }}>
                      <a href="https://wakilni.com/">
                        <img
                          src="/assets/images/wakilni-logo-v2.png"
                          height="25"
                          alt={"Wakilni"}
                        />
                      </a>
                    </h1>
                  </div>
                </div>
                <div className={"box box-default " + classes.card}>
                  <div className="box-body">
                    <h2 className="article-title" style={{ margin: "0 0 5px 0" }}>
                      Please confirm you received your cashbox #{approval.cashbox_id} amount
                    </h2>
                    <h6 className="mt-0" style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                      Order #{approval.order_id}
                    </h6>
                    <div
                      className="box-divider"
                      style={{ margin: "15px 0" }}
                    />
                    {answered ? this.renderAnswer() : this.renderRequest()}
                  </div>
                </div>
              </article>
            </div>
          )}
        </QueueAnim>
        {is_approve !== null && (
          <ConfirmResponse
            order_id={order_id}
            is_approve={is_approve}
            amounts={approval.amounts}
            handleAnswered={this.handleAnswered}
            handleFailed={this.handleFailed}
            handleClose={this.handleClose}
          />
        )}
        <EnhancedSnackBar />
      </div>
    );
  }
}

CashboxApproval.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired
};

export default withStyles(styles)(CashboxApproval);
